// Small pure formatting/derivation helpers for the messaging area (module 17
// §4.1). Pure functions over (translator, instant) so the unit tests drive
// them without a clock stub leaking into components.

import type { TFunction } from "i18next";

/** Relative age in the home.age short grammar: "now", "12 min", "3 h",
 * "2 d". Undefined for a missing or unparsable instant — the caller renders
 * nothing, never a fake zero. */
export function relativeAge(
  t: TFunction,
  iso: string | null | undefined,
  nowMs = Date.now(),
): string | undefined {
  if (iso === null || iso === undefined) return undefined;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return undefined;
  const mins = Math.max(0, Math.floor((nowMs - then) / 60_000));
  if (mins < 1) return t("messaging.age.now");
  if (mins < 60) return t("messaging.age.min", { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("messaging.age.h", { count: hours });
  return t("messaging.age.d", { count: Math.floor(hours / 24) });
}

/** Time REMAINING until an instant, same short grammar; undefined when the
 * instant is missing, unparsable or already past. */
export function timeRemaining(
  t: TFunction,
  iso: string | null | undefined,
  nowMs = Date.now(),
): string | undefined {
  if (iso === null || iso === undefined) return undefined;
  const then = Date.parse(iso);
  if (Number.isNaN(then) || then <= nowMs) return undefined;
  const mins = Math.max(1, Math.ceil((then - nowMs) / 60_000));
  if (mins < 60) return t("messaging.age.min", { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("messaging.age.h", { count: hours });
  return t("messaging.age.d", { count: Math.floor(hours / 24) });
}

/** §4.1: the WA session-countdown chip renders only when the window is open
 * and under four hours remain. */
export const WA_CHIP_THRESHOLD_MS = 4 * 60 * 60 * 1000;

export function waChipVisible(
  window: { open: boolean; closes_at: string | null },
  nowMs = Date.now(),
): boolean {
  if (!window.open || window.closes_at === null) return false;
  const closes = Date.parse(window.closes_at);
  if (Number.isNaN(closes)) return false;
  const remaining = closes - nowMs;
  return remaining > 0 && remaining < WA_CHIP_THRESHOLD_MS;
}

/** Every kind of contact address the office paints: module 17's two
 * providers and module 09's four channel kinds (spec 09 §2.4). */
export type AddressKind = "phone" | "whatsapp" | "telegram" | "email";

/**
 * §4.1/§4.5 DISPLAY masking, and spec 09 §4.2's channel list (D-307) — a
 * mirror of the server's ONE mask, `maskAddress` in
 * packages/domain-kernel/src/contact-address/index.ts (edit both). The web
 * does not depend on that package, so the forms are restated here.
 *
 * The wire is grant-shaped: holders of `messaging.address.read` (sales/fa/ga)
 * receive the FULL address, everyone else the mask. But §4.1's identity block,
 * §4.5's widget and spec 09 §4.2's channel list all paint the masked form for
 * every reader — the full value stays on the wire for a holder, never painted
 * onto a shared screen — so the inbox, the widget and the 360's channel rows
 * show one address one way. An already-masked value (the • marker) passes
 * through untouched, so this never double-masks, and it can make a screen
 * stricter than the wire, never looser.
 */
export function displayAddress(kind: AddressKind, address: string): string {
  if (address.includes("•")) return address;
  switch (kind) {
    case "phone":
    case "whatsapp": {
      const digits = address.replace(/[^0-9]/g, "");
      if (digits.length <= 5) return "+•••••";
      return `+${digits[0] ?? ""}•••••${digits.slice(-4)}`;
    }
    case "telegram": {
      const tail = address.length > 3 ? address.slice(-3) : "";
      return `chat:•••${tail}`;
    }
    case "email": {
      const at = address.lastIndexOf("@");
      return at < 0 ? "•••" : `•••@${address.slice(at + 1)}`;
    }
  }
}

export type SnoozeOption = "2h" | "tomorrow" | "next_week";

/** §4.1's three snooze presets → the `snoozed_until` instant. "Tomorrow" and
 * "next week" land at 08:00 LOCAL time — the moment a sales office reopens,
 * not a midnight nobody is at their desk for. */
export function snoozeUntil(option: SnoozeOption, now = new Date()): string {
  if (option === "2h") return new Date(now.getTime() + 2 * 60 * 60 * 1000).toISOString();
  const days = option === "tomorrow" ? 1 : 7;
  const at = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days, 8, 0, 0, 0);
  return at.toISOString();
}

/** Substitute `{{key}}` placeholders for the §4.3/§4.1 previews: the given
 * value, else the variable's sample, else the placeholder left visible —
 * an unfilled required variable must stay conspicuous, not vanish. */
export function fillTemplate(
  body: string,
  variables: readonly { key: string; sample: string }[],
  values: Readonly<Record<string, string>>,
): string {
  return body.replace(/\{\{\s*([a-z][a-z0-9_]*)\s*\}\}/g, (whole, key: string) => {
    const given = values[key];
    if (given !== undefined && given.length > 0) return given;
    const sample = variables.find((v) => v.key === key)?.sample;
    return sample !== undefined && sample.length > 0 ? sample : whole;
  });
}

/**
 * A failed message's `status_detail` in the reader's language, when the
 * detail is one the house wrote (spec 17 §4.1). The send-time guard's four
 * refusals (task #278, §3.3 step 4): `body_empty`, `body_too_long:{length}`,
 * `body_markup_invalid` and `button_url_invalid:{index}`, the index 0-based
 * and said from one. And the details the retry sweeper and the worker write
 * (review of #278, which found them shown raw): `stale_bulk`,
 * `fallback_rerouted:{new message id}`, `fallback_superseded` and
 * `worker_lost_mid_send`. And the retry sweeper's `account_disconnected` and
 * `account_suspended` (review of #216): the message was left on an account
 * that can no longer send. And the worker's `lead_not_sent` (D-585): a
 * document that follows a message whose words never went out. Undefined for any other detail, which is a
 * provider's own words (`tg:403 …`, D-022) and is shown as the server sent
 * it. `provider` is the message's own, named in the lines that depend on it.
 * Pure.
 */
export function refusedSendText(
  t: TFunction,
  detail: string | null,
  provider: "telegram" | "whatsapp",
): string | undefined {
  if (detail === null) return undefined;
  const messenger = t(`messaging.provider.${provider}`);
  switch (detail) {
    case "body_empty":
      return t("messaging.thread.refused.body_empty", { provider: messenger });
    case "body_markup_invalid":
      return t("messaging.thread.refused.body_markup_invalid");
    case "stale_bulk":
      return t("messaging.thread.refused.stale_bulk");
    case "fallback_superseded":
      return t("messaging.thread.refused.fallback_superseded");
    case "worker_lost_mid_send":
      return t("messaging.thread.refused.worker_lost_mid_send", { provider: messenger });
    case "account_disconnected":
      return t("messaging.thread.refused.account_disconnected", { provider: messenger });
    case "account_suspended":
      return t("messaging.thread.refused.account_suspended", { provider: messenger });
    case "media_unavailable":
      return t("messaging.thread.refused.media_unavailable");
    case "media_read_failed":
      return t("messaging.thread.refused.media_read_failed");
    case "lead_not_sent":
      return t("messaging.thread.refused.lead_not_sent");
    default:
      break;
  }
  const tooLong = /^body_too_long:(\d+)$/u.exec(detail);
  if (tooLong !== null) {
    return t("messaging.thread.refused.body_too_long", {
      length: tooLong[1] ?? "",
      provider: messenger,
    });
  }
  const longCaption = /^caption_too_long:(\d+)$/u.exec(detail);
  if (longCaption !== null) {
    return t("messaging.thread.refused.caption_too_long", {
      length: longCaption[1] ?? "",
      provider: messenger,
    });
  }
  const badUrl = /^button_url_invalid:(\d+)$/u.exec(detail);
  if (badUrl !== null) {
    return t("messaging.thread.refused.button_url_invalid", {
      position: String(Number(badUrl[1]) + 1),
    });
  }
  if (/^fallback_rerouted:[0-9a-f-]{36}$/u.test(detail)) {
    return t("messaging.thread.refused.fallback_rerouted");
  }
  return undefined;
}

/** The §2.5 subset's tags in every spelling the §5 PUT accepts: an ASCII
 * space, tab or line break before `href`, around the end of a tag and before
 * the `>` of a closing tag (telegram-html.ts on the server, review of #278). */
const TELEGRAM_SUBSET_TAG =
  /<(?:b|i|code)[ \t\n\r]*>|<a[ \t\n\r]+href="[^"]*"[ \t\n\r]*>|<\/(?:b|i|code|a)[ \t\n\r]*>/gu;
/** The entities a stored template body can carry: the four named ones §2.5
 * allows and numeric references. */
const TELEGRAM_ENTITY = /&(?:(lt|gt|amp|quot)|#(\d{1,7})|#x([0-9a-fA-F]{1,6}));/gu;
const NAMED_ENTITY: Readonly<Record<string, string>> = { lt: "<", gt: ">", amp: "&", quot: '"' };

/**
 * A stored Telegram template body as the customer reads it (task #278): the
 * §2.5 tags removed and the entities decoded, in one pass so `&amp;lt;` reads
 * `&lt;`. A template body is stored as the Telegram HTML it was sent as, and
 * the thread showed it with its tags and `&amp;` until #278. Anything outside
 * the subset is left as it is, so a body saved before #238 is never eaten.
 * Typed text is not passed through this, because it is stored as typed. Pure.
 */
export function telegramDisplayText(body: string): string {
  return body
    .replace(TELEGRAM_SUBSET_TAG, "")
    .replace(TELEGRAM_ENTITY, (whole, named?: string, decimal?: string, hex?: string) => {
      if (named !== undefined) return NAMED_ENTITY[named] ?? whole;
      const codePoint =
        decimal !== undefined ? Number.parseInt(decimal, 10) : Number.parseInt(hex ?? "", 16);
      const real =
        codePoint >= 1 && codePoint < 0x10ffff && !(codePoint >= 0xd800 && codePoint <= 0xdfff);
      return real ? String.fromCodePoint(codePoint) : whole;
    });
}

/** The raster types a thread bubble may draw inline (spec 17 §2.8). The
 * bubble also requires the item's `kind` to be `photo` or `sticker`
 * (`screens/inbox.tsx`); every other media item is a link. */
const INLINE_IMAGE_TYPES: ReadonlySet<string> = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

export function isInlineImageType(mimeType: string | null | undefined): boolean {
  return mimeType !== null && mimeType !== undefined && INLINE_IMAGE_TYPES.has(mimeType);
}
